import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Col, Flex, Form, Modal, Row, Tooltip, Typography } from 'antd'
import { useIntl } from 'react-intl'
import { getRequest, postRequest } from '../../../../services/base'
import { NavContext } from '../../../../context/global_context'
import useDebouncedValue from '../../../../utils/common_function'
import SelectInput from '../../../common/SelectInput'
import InputText from '../../../common/InputText'
import Button from '../../../common/Button'

const FunctionalDiagramForm = ({
  showModal,
  setShowModal,
  selectedFunctionalDiagram,
  onSave,
}) => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()

  const [searchFailed, setSearchFailed] = useState(false)

  const [salesCodeOptions, setSalesCodeOptions] = useState([])
  const [salesCodeLoading, setSalesCodeLoading] = useState(false)
  const [salesCodeSearch, setSalesCodeSearch] = useState('')
  const debouncedSalesCodeSearch = useDebouncedValue(salesCodeSearch, 500)

  const closeFormPopup = () => {
    setShowModal(false)
    form.resetFields()
  }

  const getInitialValues = () => ({
    is_create: !selectedFunctionalDiagram?.id,
    id: selectedFunctionalDiagram?.id || undefined,
    sales_code:
      selectedFunctionalDiagram?.sales_code_data?.length > 0
        ? selectedFunctionalDiagram.sales_code_data.map((item) => item.id)
        : [],
  })

  const fetchSalesCodes = async (search = '') => {
    setSalesCodeLoading(true)
    try {
      let res = await getRequest(
        `/api/sales_codes/options/?vehicle=${vehicle}&search=${search}`
      )
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setSearchFailed(true)
        setSalesCodeOptions([])
        return
      }
      setSearchFailed(false)
      const results = res?.data || []
      // The options hold the latest import of each code, while a saved code may
      // point at an older import (different id). Saved rows win so the selected
      // id stays stable and the code isn't listed twice.
      const saved = selectedFunctionalDiagram?.sales_code_data || []
      const codeKey = (item) => `${item.name}|${item.group?.name}`
      const savedKeys = new Set(saved.map(codeKey))
      // Keep the API's name ordering rather than listing saved codes first.
      const merged = [
        ...saved,
        ...results.filter((item) => !savedKeys.has(codeKey(item))),
      ].sort((a, b) => a.name.localeCompare(b.name))
      setSalesCodeOptions(
        merged.map((item) => ({ value: item.id, label: item.name, ...item }))
      )
    } finally {
      setSalesCodeLoading(false)
    }
  }

  useEffect(() => {
    if (!showModal) return
    fetchSalesCodes('')
  }, [showModal])

  useEffect(() => {
    if (debouncedSalesCodeSearch !== undefined) {
      fetchSalesCodes(debouncedSalesCodeSearch)
    }
  }, [debouncedSalesCodeSearch])

  useEffect(() => {
    if (!showModal) return
    form.setFieldsValue(getInitialValues())
  }, [showModal])

  const saveFunctionalDiagram = async (values) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'functionalDiagram.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'sales_code') {
        if (value && Array.isArray(value)) {
          value.forEach((item) => formData.append('sales_code', item))
        }
      } else if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })
    formData.append('vehicle', vehicle)

    let sub_system_id = selectedFunctionalDiagram?.id
      ? selectedFunctionalDiagram?.id
      : selectedFunctionalDiagram?.sub_system_data?.id

    let res = await postRequest(
      `/api/sub_system/functional-diagram/${sub_system_id}/`,
      formData
    )

    navDispatch({ type: 'loader', data: {}, notification: {} })

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'functionalDiagram.saveSuccess' }),
        },
      })
      onSave()
      closeFormPopup()
    } else {
      let msg = ''
      Object.keys(res).forEach((key) => {
        msg = msg ? `${msg}, ${res[key]}` : `${res[key]}`
      })
      navDispatch({
        type: 'notification',
        data: { notification_type: 'error', msg },
      })
    }
  }

  const renderSalesCodeOption = (option) => (
    <>
      <div className="bolder">{option.name || option.label}</div>
      <div>
        {option.description} ({option?.group?.name})
      </div>
    </>
  )

  const renderSalesCodeLabel = (option) => (
    <>
      <div className="bolder">{option.name || option.label}</div>
      <div>
        {option.description || ''} ({option?.group?.name || ''})
      </div>
    </>
  )

  return (
    <Modal
      open={showModal}
      title={`${selectedFunctionalDiagram?.sub_system_id} - ${selectedFunctionalDiagram?.name}`}
      width="50%"
      className="functional-diagram-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="functional-diagram-form"
          name="functional-diagram-form"
          form={form}
          layout="vertical"
          onFinish={saveFunctionalDiagram}
        >
          <InputText name="is_create" hidden={true} isRequired={false} />
          <InputText name="id" hidden={true} isRequired={false} />
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <SelectInput
                name="sales_code"
                label={intl.formatMessage({ id: 'functionalDiagram.salesCode' })}
                options={salesCodeOptions}
                notFoundContent={
                  searchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
                isRequired={false}
                allowClear={true}
                showSearch={true}
                isMulti={true}
                showLoader={salesCodeLoading}
                onInputChange={(value) => setSalesCodeSearch(value || '')}
                formatOption={renderSalesCodeOption}
                formatLabel={renderSalesCodeLabel}
                maxTagCount={10}
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip title={omittedValues.map((v) => v.label).join(', ')}>
                    <span>+{omittedValues.length}</span>
                  </Tooltip>
                )}
              />
            </Col>
            <Col span={24}>
              <Flex justify="end">
                <Button htmlType="submit" type="primary" testId="fd-sales-code-save">
                  {selectedFunctionalDiagram?.id
                    ? intl.formatMessage({ id: 'functionalDiagram.saveFunctionalDiagram' })
                    : intl.formatMessage({ id: 'functionalDiagram.createFunctionalDiagram' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default FunctionalDiagramForm
